import {EditorSelection, EditorState, type Extension, StateField, Transaction} from '@codemirror/state'
import type {Nodes} from 'hast'

import {getNodesOverlappingRange} from '../../language-tools/syntax-tree/node-range-utils'
import {setsEqual} from '../../utils'
import {focusedExtension, hasBeenFocused} from './focused'
import {getHtmlTree, htmlTreeChanged, markdownParserExtension} from './markdown-parser'
import {isMousedown, mousedownExtension} from './mousedown'

/**
 * Field that tracks selection state to use for calculating the current active node(s). This is similar to
 * `state.selection` but skips updates while the user is making a selection (while the mouse is down) and returns an
 * empty selection when the editor is not focused.
 */
const activeSelectionField = StateField.define<EditorSelection | null>({
  create: state => state.selection,
  update: (prev, transaction) => {
    return isMousedown(transaction.state)
      ? prev
      : hasBeenFocused(transaction.state)
        ? transaction.state.selection
        : null
  },
  provide: () => [focusedExtension, mousedownExtension],
})

export function getActiveSelection(state: EditorState) {
  return state.field(activeSelectionField)
}

export function activeSelectionChanged(transaction: Transaction) {
  const before = getActiveSelection(transaction.startState)
  const after = getActiveSelection(transaction.state)

  if (before === after) return false
  if (before === null || after === null) return true
  return !before.eq(after)
}

// --- Calculating the active nodes from selection state ---

// Note: We could just expose this function as `getActiveNodes`, but that would offload the memoization and change
// tracking onto the consumer. Instead we store the active nodes on a state field for reuse when nothing changes.
function calculateActiveNodes(state: EditorState) {
  const selection = getActiveSelection(state)
  const tree = getHtmlTree(state)
  const activeNodes = new Set<Nodes>()
  for (const range of selection?.ranges ?? []) {
    const overlappingNodes = getNodesOverlappingRange(tree, range.from, range.to)
    for (const node of overlappingNodes) activeNodes.add(node)

    // Blockquote edge case: Activate all children of the deepest blockquote, not just overlapping nodes.
    // This ensures blockquote delimiters stay visible when blank lines split the blockquote into multiple
    // HTML nodes (e.g., separate paragraphs and spans).
    const lowestBlockquote = overlappingNodes.findLast(node => node.type === 'element' && node.tagName === 'blockquote')
    if (lowestBlockquote?.type === 'element') {
      for (const child of lowestBlockquote.children) {
        activeNodes.add(child)
      }
    }
  }

  return activeNodes
}

/** Tracks the active node(s) in the editor, limiting recalculations to only when the editor or selection changes.  */
const activeNodesField = StateField.define<Set<Nodes>>({
  create: calculateActiveNodes,
  update: (prev, tr) => (activeSelectionChanged(tr) || htmlTreeChanged(tr) ? calculateActiveNodes(tr.state) : prev),
  provide: () => [activeSelectionField, markdownParserExtension],
})

/** Extension to calculate and track the set of active Markdown nodes in the editor. */
export const activeNodesExtension: Extension = activeNodesField

/** Get the set of active Markdown nodes */
export function getActiveNodes(state: EditorState) {
  return state.field(activeNodesField)
}

/** Check if the active nodes set changed. */
export function activeNodesChanged(transaction: Transaction): boolean {
  const before = getActiveNodes(transaction.startState)
  const after = getActiveNodes(transaction.state)

  // Checking for set equality is fast and allows us to skip updates where the selection changes but the active nodes
  // doesn't. For example, when the user clicks around or arrow-key navigates within the same block.
  return !setsEqual(before, after)
}
